/**
 * The worker protocol (ENGINE-SPEC §11.7, UX-SPEC §4.3). Plain structured-cloneable messages; the typed arrays of a
 * `result` are transferred. Types only: the main thread imports this file for its types and nothing else.
 */
import type { IngestErrorCode } from '../engine/ingest/common.ts';
import type { IntakeFormat } from '../engine/ingest/types.ts';
import type { MarkdownEdit, ReviewItem } from './md-review.ts';
import type { AnalysisSummary } from './summary.ts';
import type { BlockTable, SourceKind, ViewModel } from './viewmodel.ts';

export type ConcreteFormat = Exclude<IntakeFormat, 'url' | 'paste'>;

export type AnalyseSource =
	| { kind: 'bytes'; bytes: Uint8Array; name: string; format?: ConcreteFormat }
	| { kind: 'text'; text: string; name?: string; sample?: boolean }
	| { kind: 'fetched'; bytes: Uint8Array; contentType: string; finalUrl: string };

/** Stage keys and their weights in the one determinate bar (UX-SPEC §4.4). */
export const STAGES = [
	{ key: 'read', weight: 10 },
	{ key: 'document', weight: 10 },
	{ key: 'channels', weight: 25 },
	{ key: 'segments', weight: 30 },
	{ key: 'ensemble', weight: 20 },
	{ key: 'finalise', weight: 5 },
] as const;
export type StageKey = (typeof STAGES)[number]['key'];

export type ExportFormat = 'json' | 'md' | 'txt' | 'html' | 'docx';

export type WorkerErrorCode = IngestErrorCode | 'over-limit' | 'model' | 'internal';

/** What the client needs to list a stored result (UX-SPEC §4.6). */
export interface RecentItem {
	key: string;
	name: string;
	words: number;
	/** The index the result headline leads with: the calibrated index, or null when that is not available. */
	index: number | null;
	/** The seed-rules composite's interval. */
	ci90: [number, number];
	/** ISO 8601. */
	at: string;
	engineVersion: string;
	modelId: string;
	/** The document's language (absent in results stored before W8b: English). */
	language?: string;
	/** Made with another engine version or model: it opens only through Run again (UX-RES-04). */
	stale: boolean;
}

/** The pending or finished job a reload finds (UX-RES-03). */
export interface StoredJob {
	name: string;
	sourceKind: 'file' | 'paste' | 'sample' | 'url';
	/** ISO 8601. */
	startedAt: string;
	/** Present when a result was stored for it. */
	resultKey?: string;
}

/**
 * Marks an `analyse` as the analysis of a version of the open document (DESIGN UD-27): the result carries `name`,
 * `sourceKind` and `meta.version = { root, n }`, and nothing is stored.
 */
export interface VersionTag {
	root: string;
	n: number;
	name: string;
	sourceKind: SourceKind;
}

export type ToWorker =
	| { type: 'analyse'; id: number; source: AnalyseSource; version?: VersionTag }
	| { type: 'count'; id: number; text: string }
	| { type: 'cancel'; id: number }
	/** `payload` only when the worker does not hold this `docHash` (after a restore from storage). */
	| { type: 'export'; id: number; format: ExportFormat; docHash?: string; payload?: ViewModel }
	| { type: 'find'; id: number; query: string; docHash?: string; payload?: ViewModel }
	/** Reply: `probed`. Looks for the stored current job. */
	| { type: 'probe'; id: number }
	/** Reply: `result` (with `restoredAt`) or `error`. */
	| { type: 'restore'; id: number; key: string }
	/** Reply: `recent`. */
	| { type: 'recent'; id: number }
	/** Reply: `recent` after removing one stored result. */
	| { type: 'remove'; id: number; key: string }
	/** Forgets the current job pointer (New analysis, Discard); stored results stay. Reply: `recent`. */
	| { type: 'discard'; id: number }
	/** Analyse the stored source of the current job (or of result `key`) again ("Run again"). */
	| { type: 'rerun'; id: number; key?: string }
	/**
	 * Analyse `text` as pasted text and reply `summary` (UD-22): nothing is stored and the open document is not replaced.
	 * `progress` messages with the same id report the stages. Cancelled like `analyse`, by `cancel` or by ending the worker.
	 * With `working` the text is first taken to its working text (the bench's fixed point, `workingText`).
	 */
	| { type: 'summarise'; id: number; text: string; working?: boolean }
	/** No reply. The worker keeps `text` for find as the text of the result `docHash` (a version the client switched to). */
	| { type: 'hold'; id: number; docHash: string; text: string }
	/**
	 * Reply: `markdown`. Version `text` of the document `docHash` as Markdown (DESIGN UD-30): from the stored upload when it was
	 * Markdown, else from the document model. `original` and `layout` are version 0's reading text and blocks.
	 */
	| {
			type: 'versionMarkdown';
			id: number;
			docHash: string;
			original: string;
			layout: Pick<BlockTable, 'count' | 'kind' | 'level' | 'charStart' | 'charEnd' | 'para'>;
			text: string;
			/** The operator's edits of this version's paragraphs (UD-31): each replaces its paragraph's automatic Markdown. */
			edits?: MarkdownEdit[];
	  }
	/**
	 * Reply: `review`. The rewritten paragraphs of version `text` that can carry inline markup, each with the original's
	 * Markdown, the automatic Markdown of the new text, what became of every inline span, and the operator's edit (UD-31).
	 */
	| {
			type: 'markdownReview';
			id: number;
			docHash: string;
			original: string;
			layout: Pick<BlockTable, 'count' | 'kind' | 'level' | 'charStart' | 'charEnd' | 'para'>;
			text: string;
			edits?: MarkdownEdit[];
	  };

export interface IntakeInfo {
	name: string;
	words: number;
	/** The format as given (`url` and `paste` keep their own name). */
	format: IntakeFormat;
	dispatchedFormat?: ConcreteFormat;
	bytes: number;
	parseMs: number;
	pages?: number;
	title?: string;
	finalUrl?: string;
	warnings: string[];
}

export type FromWorker =
	| {
			type: 'progress';
			id: number;
			stage: StageKey;
			/** Overall percent 0–100 by the stage weights. */
			pct: number;
			detail?: { page: number; pages: number };
	  }
	| { type: 'intake'; id: number; intake: IntakeInfo }
	| { type: 'result'; id: number; payload: ViewModel; restoredAt?: string }
	| { type: 'probed'; id: number; job: StoredJob | null }
	| { type: 'recent'; id: number; items: RecentItem[] }
	/** After a result was (not) saved for restoring; `ok: false` when storage is unavailable or full. */
	| { type: 'stored'; id: number; ok: boolean }
	| { type: 'cancelled'; id: number }
	| { type: 'counted'; id: number; words: number }
	| { type: 'error'; id: number; code: WorkerErrorCode; message: string }
	| { type: 'exported'; id: number; format: ExportFormat; blob: Blob; filename: string }
	| { type: 'found'; id: number; ranges: Uint32Array; total: number }
	/** The reply to `summarise`. */
	| { type: 'summary'; id: number; summary: AnalysisSummary }
	/**
	 * The reply to `versionMarkdown`: `changed` blocks; `fromSource` when built from the uploaded Markdown; `dropped`, the inline
	 * spans of changed paragraphs that could not be carried (their paragraph, 1-based, and their text as written).
	 */
	| {
			type: 'markdown';
			id: number;
			markdown: string;
			changed: number;
			fromSource: boolean;
			dropped: { para: number; text: string }[];
			/** Moved onto new words (the review of moved and dropped markup reads `versionMarkdown`'s `inline`). */
			moved: number;
	  }
	/** The reply to `markdownReview`: `moved` and `dropped` count the spans the operator has not settled by an edit. */
	| { type: 'review'; id: number; items: ReviewItem[]; moved: number; dropped: number };
